import { jsxs, jsx } from 'react/jsx-runtime';
import { useEffect, useState } from 'react';
import { useApi, API } from '@plumeuk/shapeshift-identity';
import 'x2js';
import '@mui/material';

const useLesson = (courseSlug, lessonSlug, data, populate) => {
    const [apiResult, getData, setLesson] = useApi(data || !courseSlug || !lessonSlug ? null : { url: API.lesson(courseSlug, lessonSlug), data: { populate } }, data);
    const getLesson = (courseSlugOverride, lessonSlugOverride, configOverride) => {
        const _lessonSlug = lessonSlugOverride ?? lessonSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_lessonSlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.lesson(_courseSlug, _lessonSlug) }
            : API.lesson(_courseSlug, _lessonSlug));
    };
    useEffect(() => data && setLesson(data), [data]);
    return { lesson: apiResult.data, apiResult, getLesson, setLesson };
};

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
console.error.bind(null) ;

//Get module in tree
//Default latest active, if request passed returns the matching module or undefined
const getModule = (course, request, includeUnavailable) => {
    const n = (modules) => {
        let module;
        modules.forEach(e => {
            if (module)
                return;
            if (e.type !== "section" && ((request && request.slug == e.slug && request.type == e.type)
                || (!request && !e.complete && (includeUnavailable || !e.availableFrom || e.availableFrom < new Date())))) {
                module = e;
                return;
            }
            else if (e.modules) {
                module = n(e.modules);
                if (module)
                    return;
            }
        });
        return module;
    };
    if (!course || !course.modules)
        return;
    return n(course?.modules);
};

const LessonDataProvider = ({ children, courseSlug, lessonSlug, data, populate, curriculum }) => {
    const dataHook = useLesson(courseSlug, lessonSlug, data, populate);
    const { lesson, getLesson, setLesson } = dataHook;
    useEffect(() => {
        if (lessonSlug && courseSlug && !data)
            getLesson(courseSlug, lessonSlug, populate ? { data: { populate } } : undefined);
    }, [lessonSlug, courseSlug, populate]);
    useEffect(() => {
        if (curriculum && lesson) {
            const module = getModule(curriculum, { slug: lesson.slug, type: "lesson" });
            if (module && module?.complete !== lesson.complete) {
                setLesson({ ...lesson, complete: module?.complete });
            }
        }
    }, [curriculum]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle lesson ", lesson?.title ?? "...loading"] });
};

const useQuiz = (courseSlug, quizSlug, data) => {
    const [apiResult, getData, setQuiz] = useApi(data || !courseSlug || !quizSlug ? null : API.quiz(courseSlug, quizSlug), data);
    const getQuiz = (courseSlugOverride, quizSlugOverride, configOverride) => {
        const _quizSlug = quizSlugOverride ?? quizSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_quizSlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.quiz(_courseSlug, _quizSlug) }
            : API.quiz(_courseSlug, _quizSlug));
    };
    useEffect(() => data && setQuiz(data), [data]);
    return { quiz: apiResult.data, apiResult, getQuiz, setQuiz };
};

const QuizDataProvider = ({ children, courseSlug, quizSlug, data, curriculum }) => {
    const dataHook = useQuiz(courseSlug, quizSlug, data);
    const { getQuiz, setQuiz, quiz } = dataHook;
    useEffect(() => {
        if (quizSlug && courseSlug && !data)
            getQuiz(courseSlug, quizSlug);
    }, [quizSlug, courseSlug]);
    useEffect(() => {
        if (curriculum && quiz) {
            const module = getModule(curriculum, { slug: quiz.slug, type: "quiz" });
            if (module && module?.complete !== quiz.complete) {
                setQuiz({ ...quiz, complete: module?.complete });
            }
        }
    }, [curriculum]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle quiz ", quiz?.title ?? "...loading"] });
};

const useCourseCurriculum = (courseSlug, data) => {
    const [apiResult, getData] = useApi(data || !courseSlug ? null : API.course(courseSlug), data);
    const [course, setCourse] = useState(data);
    useEffect(() => setCourse(apiResult.data), [apiResult.data]);
    useEffect(() => setCourse(data), [data]);
    const setModuleComplete = (moduleRequest, status) => {
        //handle internal update
        if (!data && course) {
            setCourse(prev => {
                const module = getModule(course, moduleRequest);
                if (!prev || !module || module.complete == status)
                    return prev;
                if (module.progression !== false) {
                    prev.modulesCompleteCount += (status ? 1 : -1);
                    prev.percentCompleted = Math.round((prev.modulesCompleteCount / prev.modulesCount) * 100);
                }
                module.complete = status;
                return { ...prev };
            });
        }
    };
    const setModule = (moduleRequest, prop, value) => {
        if (!data && course) {
            setCourse(prev => {
                const module = getModule(course, moduleRequest);
                if (prop === "complete")
                    throw new Error("Use setModuleComplete instead");
                if (!prev || !module || module[prop] == value)
                    return prev;
                module[prop] = value;
                return { ...prev };
            });
        }
    };
    const getCourse = (slugOverride, configOverride) => {
        const slug = slugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.course(slug) }
            : API.course(slugOverride ?? slug));
    };
    return { course, apiResult, getCourse, setCourse, setModuleComplete, setModule };
};

const useCourseCurriculumPublic = (courseSlug, data) => {
    const [apiResult, getData] = useApi(data || !courseSlug ? null : API.courseCurriculum(courseSlug), data);
    const [courseCurriculumPublic, setCourseCurriculumPublic] = useState(data);
    useEffect(() => setCourseCurriculumPublic(apiResult.data), [apiResult.data]);
    useEffect(() => setCourseCurriculumPublic(data), [data]);
    const getCourseCurriculumPublic = (slugOverride, configOverride) => {
        const slug = slugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.courseCurriculum(slug) }
            : API.courseCurriculum(slugOverride ?? slug));
    };
    return { courseCurriculumPublic, apiResult, getCourseCurriculumPublic, setCourseCurriculumPublic };
};

const useCourseCertificate = (courseSlug, data) => {
    const [apiResult, getData] = useApi(data || !courseSlug ? null : API.courseCertificate(courseSlug), data);
    const [certificate, setCertificate] = useState();
    useEffect(() => setCertificate(apiResult.data), [apiResult.data]);
    useEffect(() => setCertificate(data), [data]);
    const getCertificate = (slugOverride, configOverride) => {
        const slug = slugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.courseCertificate(slug) }
            : API.courseCertificate(slug));
    };
    useEffect(() => data && setCertificate(data), [data]);
    return { certificate, apiResult, getCertificate, setCertificate };
};

const useCourseAvailableModuleProgression = (slug, data) => {
    const [apiResult, getData, setCourseAvailableModuleProgression] = useApi(data || !slug ? null : API.courseAvailableModuleProgression(slug), data);
    const getCourseAvailableModuleProgression = (slugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        if (!_slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.courseAvailableModuleProgression(_slug) }
            : API.courseAvailableModuleProgression(_slug));
    };
    useEffect(() => {
        if (data)
            return;
        getCourseAvailableModuleProgression(slug);
    }, [slug, data]);
    useEffect(() => data && setCourseAvailableModuleProgression(data), [data]);
    return { result: apiResult.data, apiResult, getCourseAvailableModuleProgression, setCourseAvailableModuleProgression };
};

const useCourses = (showProgress, data) => {
    const [apiResult, getData, setCourses] = useApi(data ? null : API.courses(showProgress ?? true), data);
    const getCourses = (showProgressOveride, configOverride) => {
        const _showProgress = showProgressOveride ?? showProgress;
        getData(configOverride
            ? { ...configOverride, url: API.courses(_showProgress ?? true) }
            : API.courses(_showProgress ?? true));
    };
    useEffect(() => data && setCourses(data), [data]);
    return { courses: apiResult.data, apiResult, getCourses, setCourses };
};

const useQuizQuestions = (courseSlug, quizSlug, data) => {
    const [apiResult, getData, setQuizQuestions] = useApi(data || !courseSlug || !quizSlug ? null : API.quizQuestions(courseSlug, quizSlug), data);
    const getQuizQuestions = (courseSlugOverride, quizSlugOverride, configOverride) => {
        const _quizSlug = quizSlugOverride ?? quizSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_quizSlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.quizQuestions(_courseSlug, _quizSlug) }
            : API.quizQuestions(_courseSlug, _quizSlug));
    };
    useEffect(() => data && setQuizQuestions(data), [data]);
    return { quizQuestions: apiResult.data, apiResult, getQuizQuestions, setQuizQuestions };
};

const useSurvey = (courseSlug, surveySlug, data) => {
    const [apiResult, getData, setSurvey] = useApi(data || !courseSlug || !surveySlug ? null : API.survey(courseSlug, surveySlug), data);
    const getSurvey = (courseSlugOverride, surveySlugOverride, configOverride) => {
        const _surveySlug = surveySlugOverride ?? surveySlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_surveySlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.survey(_courseSlug, _surveySlug) }
            : API.survey(_courseSlug, _surveySlug));
    };
    useEffect(() => data && setSurvey(data), [data]);
    return { survey: apiResult.data, apiResult, getSurvey, setSurvey };
};

const useSurveyQuestions = (courseSlug, surveySlug, data) => {
    const [apiResult, getData, setSurveyQuestions] = useApi(data || !courseSlug || !surveySlug ? null : API.surveyQuestions(courseSlug, surveySlug), data);
    const getSurveyQuestions = (courseSlugOverride, surveySlugOverride, configOverride) => {
        const _surveySlug = surveySlugOverride ?? surveySlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_surveySlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.surveyQuestions(_courseSlug, _surveySlug) }
            : API.surveyQuestions(_courseSlug, _surveySlug));
    };
    useEffect(() => {
        if (data)
            setSurveyQuestions(data);
        else
            getSurveyQuestions(courseSlug, surveySlug);
    }, [data, surveySlug, courseSlug]);
    return { surveyQuestions: apiResult.data, apiResult, getSurveyQuestions, setSurveyQuestions };
};

const useSetModuleCompletion = () => {
    const [apiResult, getData] = useApi(null);
    const setComplete = (courseSlug, moduleSlug, type, status, config) => getData({
        ...(config ?? {}),
        url: API.moduleComplete(courseSlug, moduleSlug, type, status),
        method: "POST",
        cache: false
    });
    return { response: apiResult.data, apiResult, setComplete };
};

const useMarkQuizAnswers = () => {
    const [apiResult, getData] = useApi(null);
    const markAnswers = (courseSlug, quizSlug, answers, config) => getData({
        ...(config ?? {}),
        data: answers,
        url: API.quizMark(courseSlug, quizSlug),
        method: "POST"
    });
    return { response: apiResult.data, apiResult, markAnswers };
};

const useMarkedQuizResult = (courseSlug, quizSlug, resultId, data) => {
    const [apiResult, getData, setMarkedQuizResult] = useApi(data || !courseSlug || !quizSlug ? null : API.quizMarkResult(courseSlug, quizSlug, resultId), data);
    const getMarkedQuizResult = (courseSlugOverride, quizSlugOverride, idOverride, configOverride) => {
        const _courseSlug = courseSlugOverride ?? courseSlug;
        const _quizSlug = quizSlugOverride ?? quizSlug;
        const _resultId = idOverride ?? resultId;
        if (!_quizSlug || !_resultId || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.quizMarkResult(_courseSlug, _quizSlug, _resultId) }
            : API.quizMarkResult(_courseSlug, _quizSlug, _resultId));
    };
    useEffect(() => data && setMarkedQuizResult(data), [data]);
    return { markedQuizResult: apiResult.data, apiResult, getMarkedQuizResult, setMarkedQuizResult };
};

const useLibrary = (search, data) => {
    const [apiResult, getData, setLibrary] = useApi(data ? null : { url: API.library, data: search }, data);
    const getLibrary = (searchOverride, configOverride) => {
        const data = searchOverride !== undefined ? searchOverride : search;
        getData(configOverride
            ? { ...configOverride, url: API.library, data }
            : { url: API.library, data });
    };
    useEffect(() => data && setLibrary(data), [data]);
    return { items: apiResult.data, apiResult, getLibrary, setLibrary };
};

const useLibraryCourses = (search, data) => {
    const [apiResult, getData, setCourses] = useApi(data ? null : { url: API.libraryCourses, data: search }, data);
    const getCourses = (searchOverride, configOverride) => {
        const data = searchOverride !== undefined ? searchOverride : search;
        getData(configOverride
            ? { ...configOverride, url: API.libraryCourses, data }
            : { url: API.libraryCourses, data });
    };
    useEffect(() => data && setCourses(data), [data]);
    return { courses: apiResult.data, apiResult, getCourses, setCourses };
};

const useModulesUpcoming = (courseSlug, data) => {
    const [apiResult, getData, setUpcoming] = useApi(data || !courseSlug ? null : API.upcoming(courseSlug), data);
    const getUpcoming = (courseSlugOverride, configOverride) => {
        const slug = courseSlugOverride ?? courseSlug;
        if (!slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.upcoming(slug) }
            : API.upcoming(slug));
    };
    useEffect(() => data && setUpcoming(data), [data]);
    return { upcoming: apiResult.data, apiResult, getUpcoming, setUpcoming };
};

const useCommentPoster = () => {
    const [apiResult, getData] = useApi(null);
    const postComment = (comment, moduleType, courseSlug, moduleSlug, config) => getData({
        ...(config ?? {}),
        data: comment,
        url: API.comments(courseSlug, moduleType, moduleSlug),
        method: "POST"
    });
    return { response: apiResult.data, apiResult, postComment };
};

const useLibraryCourse = (slug, groupSlug, data) => {
    const [apiResult, getData, setCourse] = useApi(data || !slug ? null : API.libraryCourse(slug, groupSlug), data);
    const getCourse = (slugOverride, groupSlugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        const _groupSlugOverride = groupSlugOverride ?? groupSlug;
        if (!_slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.libraryCourse(_slug, _groupSlugOverride) }
            : API.libraryCourse(_slug, _groupSlugOverride));
    };
    useEffect(() => data && setCourse(data), [data]);
    return { course: apiResult.data, apiResult, getCourse, setCourse };
};

const useLibraryGroup = (slug, showProgress, data) => {
    const [apiResult, getData, setGroup] = useApi(data || !slug ? null : API.libraryGroup(slug), data);
    const getGroup = (slugOverride, showProgressOveride, configOverride) => {
        const _slug = slugOverride ?? slug;
        const _showProgress = showProgressOveride ?? showProgress;
        if (!_slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.libraryGroup(_slug, _showProgress ?? true) }
            : API.libraryGroup(_slug, _showProgress ?? true));
    };
    useEffect(() => data && setGroup(data), [data]);
    return { group: apiResult.data, apiResult, getGroup, setGroup };
};

const useNotes = (courseSlug, moduleType, moduleSlug, data) => {
    const [apiResult, getData, setNotes] = useApi((data || !courseSlug || !moduleSlug || !moduleType) ? null : API.notes(courseSlug, moduleType, moduleSlug), data);
    const getNotes = (courseSlugOverride, typeOverride, moduleSlugOverride, configOverride) => {
        const _type = typeOverride ?? moduleType;
        const _moduleSlug = moduleSlugOverride ?? moduleSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_courseSlug || !_moduleSlug || !_type)
            return;
        const url = API.notes(_courseSlug, _type, _moduleSlug);
        getData(configOverride ? { ...configOverride, url } : url);
    };
    useEffect(() => data && setNotes(data), [data]);
    return { notes: apiResult.data, apiResult, getNotes, setNotes };
};

const useWebinar = (courseSlug, webinarSlug, data) => {
    const [apiResult, getData, setWebinar] = useApi(data || !courseSlug || !webinarSlug ? null : API.webinar(courseSlug, webinarSlug), data);
    const getWebinar = (courseSlugOverride, webinarSlugOverride, configOverride) => {
        const _webinarSlug = webinarSlugOverride ?? webinarSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_webinarSlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.webinar(_courseSlug, _webinarSlug) }
            : API.webinar(_courseSlug, _webinarSlug));
    };
    useEffect(() => data && setWebinar(data), [data]);
    return { webinar: apiResult.data, apiResult, getWebinar, setWebinar };
};

const useNotePoster = () => {
    const [apiResult, getData] = useApi(null);
    const postNote = (note, courseSlug, moduleType, moduleSlug, config) => getData({
        ...(config ?? {}),
        data: note,
        url: API.notes(courseSlug, moduleType, moduleSlug),
        method: "POST"
    });
    const editNote = (id, content, config) => getData({
        ...(config ?? {}),
        data: { content },
        url: API.noteModify(id),
        method: "PATCH"
    });
    const deleteNote = (id, _content, config) => getData({
        ...(config ?? {}),
        url: API.noteModify(id),
        method: "DELETE"
    });
    return { response: apiResult.data, apiResult, postNote, editNote, deleteNote };
};

const useSetCourseEnrollment = () => {
    const [apiResult, getData] = useApi(null);
    const setEnroll = (courseSlug, config) => getData({
        ...(config ?? {}),
        url: API.courseEnroll(courseSlug),
        method: "PUT"
    });
    return { response: apiResult.data, apiResult, setEnroll };
};

const useOwnedGroup = (slug, courseSlug, data) => {
    const [apiResult, getData, setGroup] = useApi(null, data);
    const getGroup = (slugOverride, courseSlugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_slug)
            return;
        getData({
            ...(configOverride ?? {}),
            url: API.ownedGroup(_slug),
            data: _courseSlug ? { courseSlug: _courseSlug } : undefined
        });
    };
    useEffect(() => {
        if (data)
            return;
        getGroup(slug, courseSlug);
    }, [slug, courseSlug, data]);
    useEffect(() => data && setGroup(data), [data]);
    return { group: apiResult.data, apiResult, getGroup, setGroup };
};

const useOwnedGroups = (data) => {
    const [apiResult, getData, setGroups] = useApi(data ? null : API.ownedGroups, data);
    const getGroups = (configOverride) => getData(configOverride
        ? { ...configOverride, url: API.ownedGroups }
        : API.ownedGroups);
    useEffect(() => data && setGroups(data), [data]);
    return { groups: apiResult.data, apiResult, getGroups, setGroups };
};

const useMemberedGroup = (slug, courseSlug, data) => {
    const [apiResult, getData, setGroup] = useApi(null, data);
    const getGroup = (slugOverride, courseSlugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_slug)
            return;
        getData({
            ...(configOverride ?? {}),
            url: API.memberedGroup(_slug),
            data: _courseSlug ? { courseSlug: _courseSlug } : undefined
        });
    };
    useEffect(() => {
        if (data)
            return;
        getGroup(slug, courseSlug);
    }, [slug, courseSlug, data]);
    useEffect(() => data && setGroup(data), [data]);
    return { result: apiResult.data, apiResult, getGroup, setGroup };
};

const useMemberedGroups = (data) => {
    const [apiResult, getData, setGroups] = useApi(data ? null : API.memberedGroups, data);
    const getGroups = (configOverride) => getData(configOverride
        ? { ...configOverride, url: API.memberedGroups }
        : API.memberedGroups);
    useEffect(() => data && setGroups(data), [data]);
    return { groups: apiResult.data, apiResult, getGroups, setGroups };
};

const QuizQuestionsDataProvider = ({ children, courseSlug, quizSlug, data }) => {
    const dataHook = useQuizQuestions(courseSlug, quizSlug, data);
    const { getQuizQuestions, quizQuestions } = dataHook;
    useEffect(() => {
        if (quizSlug && !data)
            getQuizQuestions(courseSlug, quizSlug);
    }, [quizSlug]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle quiz questions (count: ", quizQuestions?.length ?? "...loading", ")"] });
};

const CourseCurriculumDataProvider = ({ children, courseSlug, data }) => {
    const dataHook = useCourseCurriculum(courseSlug, data);
    useEffect(() => {
        if (courseSlug && !data)
            dataHook.getCourse(courseSlug);
    }, [courseSlug]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle course curriculum ", dataHook.course?.title ?? "...loading"] });
};

const CourseCertificateDataProvider = ({ children, courseSlug, data }) => {
    const dataHook = useCourseCertificate(courseSlug, data);
    useEffect(() => {
        if (courseSlug && !data)
            dataHook.getCertificate(courseSlug);
    }, [courseSlug]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle course certificate ", dataHook.certificate?.printedTitle ?? "...loading"] });
};

const CoursesDataProvider = ({ children, showProgress, data }) => {
    const dataHook = useCourses(showProgress, data);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle courses" });
};

const MarkedQuizResultDataProvider = ({ children, courseSlug, quizSlug, resultId, data }) => {
    const dataHook = useMarkedQuizResult(courseSlug, quizSlug, resultId, data);
    const { getMarkedQuizResult } = dataHook;
    useEffect(() => {
        if (courseSlug && quizSlug && resultId && !data)
            getMarkedQuizResult(courseSlug, quizSlug, resultId);
    }, [resultId, quizSlug, courseSlug]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle marked quiz result" });
};

const LibraryCoursesDataProvider = ({ search, children, data }) => {
    const dataHook = useLibraryCourses(search, data);
    const { getCourses } = dataHook;
    useEffect(() => {
        if (search !== undefined && !data)
            getCourses(search);
    }, [search]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle library courses" });
};

const useComments = (courseSlug, moduleType, moduleSlug, data) => {
    const [apiResult, getData, setComments] = useApi((data || !courseSlug || !moduleSlug || !moduleType) ? null : API.comments(courseSlug, moduleType, moduleSlug), data);
    const getComments = (courseSlugOverride, typeOverride, moduleSlugOverride, configOverride) => {
        const _type = typeOverride ?? moduleType;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        const _moduleSlug = moduleSlugOverride ?? moduleSlug;
        if (!_courseSlug || !_moduleSlug || !_type)
            return;
        const url = API.comments(_courseSlug, _type, _moduleSlug);
        getData(configOverride ? { ...configOverride, url } : url);
    };
    useEffect(() => data && setComments(data), [data]);
    return { comments: apiResult.data, apiResult, getComments, setComments };
};

const CommentsDataProvider = ({ children, courseSlug, moduleType, moduleSlug, data }) => {
    const dataHook = useComments(courseSlug, moduleType, moduleSlug, data);
    const { getComments, comments } = dataHook;
    useEffect(() => {
        if (!data)
            getComments(courseSlug, moduleType, moduleSlug);
    }, [courseSlug, moduleType, moduleSlug]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle comments ", comments?.length ?? "...loading"] });
};

const LibraryCourseDataProvider = ({ slug, groupSlug, children, data }) => {
    const dataHook = useLibraryCourse(slug, groupSlug, data);
    const { getCourse } = dataHook;
    useEffect(() => {
        if (slug && !data)
            getCourse(slug, groupSlug);
    }, [slug, groupSlug]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle library course" });
};

const LibraryGroupDataProvider = ({ slug, showProgress, children, data }) => {
    const dataHook = useLibraryGroup(slug, showProgress, data);
    const { getGroup } = dataHook;
    useEffect(() => {
        if (slug && !data)
            getGroup(slug, showProgress);
    }, [slug, showProgress]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle library group" });
};

const ModuleUpcomingDataProvider = ({ children, courseSlug, data }) => {
    const dataHook = useModulesUpcoming(courseSlug, data);
    const { getUpcoming } = dataHook;
    useEffect(() => {
        if (courseSlug && !data)
            getUpcoming(courseSlug);
    }, [courseSlug]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle upcoming" });
};

const NotesDataProvider = ({ children, courseSlug, moduleType, moduleSlug, data }) => {
    const dataHook = useNotes(courseSlug, moduleType, moduleSlug, data);
    const { getNotes, notes } = dataHook;
    useEffect(() => {
        if (!data)
            getNotes(courseSlug, moduleType, moduleSlug);
    }, [courseSlug, moduleType, moduleSlug]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle notes ", notes?.length ?? "...loading"] });
};

const OwnedGroupsDataProvider = ({ children, data }) => {
    const dataHook = useOwnedGroups(data);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle groups" });
};

//course slug will filter result sumary by course
const OwnedGroupDataProvider = ({ children, slug, courseSlug, data }) => {
    const dataHook = useOwnedGroup(slug, courseSlug, data);
    const { group, getGroup } = dataHook;
    useEffect(() => {
        if (slug && !data)
            getGroup(slug, courseSlug);
    }, [slug, courseSlug, data]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle group ", group?.title ?? "...loading"] });
};

const MemberedGroupsDataProvider = ({ children, data }) => {
    const dataHook = useMemberedGroups(data);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle groups" });
};

//course slug will filter result sumary by course
const MemberedGroupDataProvider = ({ children, slug, data }) => {
    const dataHook = useMemberedGroup(slug);
    const { result, getGroup } = dataHook;
    useEffect(() => {
        if (slug && !data)
            getGroup(slug);
    }, [slug]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle group ", result?.title ?? "...loading"] });
};

const useScorm = (courseSlug, scormSlug, data) => {
    const [apiResult, getData, setScorm] = useApi(data || !courseSlug || !scormSlug ? null : API.scorm(courseSlug, scormSlug), data);
    const getScorm = (courseSlugOverride, scormSlugOverride, configOverride) => {
        const _scormSlug = scormSlugOverride ?? scormSlug;
        const _courseSlug = courseSlugOverride ?? courseSlug;
        if (!_scormSlug || !_courseSlug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.scorm(_courseSlug, _scormSlug) }
            : API.scorm(_courseSlug, _scormSlug));
    };
    useEffect(() => data && setScorm(data), [data]);
    return { scorm: apiResult.data, apiResult, getScorm, setScorm };
};

const ScormDataProvider = ({ children, courseSlug, scormSlug, data, curriculum }) => {
    const dataHook = useScorm(courseSlug, scormSlug, data);
    const { getScorm, setScorm, scorm } = dataHook;
    useEffect(() => {
        if (scormSlug && courseSlug && !data)
            getScorm(courseSlug, scormSlug);
    }, [scormSlug, courseSlug]);
    useEffect(() => {
        if (curriculum && scorm) {
            const module = getModule(curriculum, { slug: scorm.slug, type: "scorm" });
            if (module && module?.complete !== scorm.complete) {
                setScorm({ ...scorm, complete: module?.complete });
            }
        }
    }, [curriculum]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle scorm ", scorm?.title ?? "...loading"] });
};

const WebinarDataProvider = ({ children, courseSlug, webinarSlug, data }) => {
    const dataHook = useWebinar(courseSlug, webinarSlug, data);
    const { getWebinar, webinar } = dataHook;
    useEffect(() => {
        if (webinarSlug && courseSlug && !data)
            getWebinar(courseSlug, webinarSlug);
    }, [webinarSlug, courseSlug]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle webinar ", webinar?.title ?? "...loading"] });
};

const SurveyDataProvider = ({ children, courseSlug, surveySlug, data, curriculum }) => {
    const dataHook = useSurvey(courseSlug, surveySlug, data);
    const { getSurvey, survey } = dataHook;
    useEffect(() => {
        if (surveySlug && courseSlug && !data)
            getSurvey(courseSlug, surveySlug);
    }, [surveySlug, courseSlug]);
    useEffect(() => {
        if (curriculum && survey) {
            getSurvey(undefined, undefined, { cache: false });
        }
    }, [curriculum]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle survey ", survey?.title ?? "...loading"] });
};

const SurveyQuestionsDataProvider = ({ children, courseSlug, surveySlug, data }) => {
    const dataHook = useSurveyQuestions(courseSlug, surveySlug, data);
    const { getSurveyQuestions, surveyQuestions } = dataHook;
    useEffect(() => {
        if (surveySlug && !data)
            getSurveyQuestions(courseSlug, surveySlug);
    }, [surveySlug]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle survey questions (count: ", surveyQuestions?.length ?? "...loading", ")"] });
};

const CourseAvailableModuleProgressionDataProvider = ({ children, slug, data }) => {
    const dataHook = useCourseAvailableModuleProgression(slug, data);
    useEffect(() => {
        if (slug && !data)
            dataHook.getCourseAvailableModuleProgression(slug);
    }, [slug]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle course module progression." });
};

const useCourseModules = (courseSlug, data) => {
    const [apiResult, getData, setExtendedCourseModules] = useApi(data ? null : API.courseModules(courseSlug), data);
    const getCourseModules = (configOverride) => getData(configOverride
        ? { ...configOverride, url: API.courseModules(courseSlug) }
        : API.courseModules(courseSlug));
    useEffect(() => data && setExtendedCourseModules(data), [data]);
    return { courseModules: apiResult.data, apiResult, getCourseModules, setExtendedCourseModules };
};

const CourseModulesDataProvider = ({ children, courseSlug, data }) => {
    const dataHook = useCourseModules(courseSlug, data);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle modules" });
};

const CourseCurriculumPublicDataProvider = ({ children, courseSlug, data }) => {
    const dataHook = useCourseCurriculumPublic(courseSlug, data);
    useEffect(() => {
        if (courseSlug && !data)
            dataHook.getCourseCurriculumPublic(courseSlug);
    }, [courseSlug]);
    return children?.(dataHook)
        ?? jsxs("div", { children: ["Please implement children to handle course curriculum ", dataHook.courseCurriculumPublic?.title ?? "...loading"] });
};

const LibraryDataProvider = ({ search, children, data }) => {
    const dataHook = useLibrary(search, data);
    const { getLibrary } = dataHook;
    useEffect(() => {
        if (search !== undefined && !data)
            getLibrary(search);
    }, [search]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle library items" });
};

const ModuleCompleteActionProvider = ({ children, actionOverride }) => {
    const { setComplete, apiResult, response } = useSetModuleCompletion();
    const complete = actionOverride ?? ((courseSlug, moduleSlug, type) => setComplete(courseSlug, moduleSlug, type));
    return children
        ? children(complete, response, apiResult)
        : jsx("div", { children: "Please implement children to utilize action" });
};

const MarkQuizActionProvider = ({ children, actionOverride }) => {
    const dataHook = useMarkQuizAnswers();
    const { markAnswers: baseAction } = dataHook;
    dataHook.markAnswers = actionOverride ?? baseAction;
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to utilize MarkQuizActionProvider action" });
};

const CommentPostActionProvider = ({ children, actionOverride }) => {
    const dataHook = useCommentPoster();
    const { postComment: baseAction } = dataHook;
    dataHook.postComment = actionOverride ?? baseAction;
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to utilize CommentPostActionProvider action" });
};

const NotePostActionProvider = ({ children, postActionOverride, editNoteActionOverride, deleteNoteActionOverrid }) => {
    const dataHook = useNotePoster();
    const { postNote: basePostAction, editNote: baseEditAction, deleteNote: baseDeleteAction } = dataHook;
    dataHook.postNote = postActionOverride ?? basePostAction;
    dataHook.editNote = editNoteActionOverride ?? baseEditAction;
    dataHook.deleteNote = deleteNoteActionOverrid ?? baseDeleteAction;
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to utilize NotePostActionProvider action" });
};

const CourseEnrollActionProvider = ({ children, actionOverride }) => {
    const { setEnroll, apiResult, response } = useSetCourseEnrollment();
    const enroll = actionOverride ?? ((slug) => setEnroll(slug));
    return children
        ? children(enroll, response, apiResult)
        : jsx("div", { children: "Please implement children to utilize action" });
};

export { CommentPostActionProvider, CommentsDataProvider, CourseAvailableModuleProgressionDataProvider, CourseCertificateDataProvider, CourseCurriculumDataProvider, CourseCurriculumPublicDataProvider, CourseEnrollActionProvider, CourseModulesDataProvider, CoursesDataProvider, LessonDataProvider, LibraryCourseDataProvider, LibraryCoursesDataProvider, LibraryDataProvider, LibraryGroupDataProvider, MarkQuizActionProvider, MarkedQuizResultDataProvider, MemberedGroupDataProvider, MemberedGroupsDataProvider, ModuleCompleteActionProvider, ModuleUpcomingDataProvider, NotePostActionProvider, NotesDataProvider, OwnedGroupDataProvider, OwnedGroupsDataProvider, QuizDataProvider, QuizQuestionsDataProvider, ScormDataProvider, SurveyDataProvider, SurveyQuestionsDataProvider, WebinarDataProvider };
